import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { geodeticToENU } from "../utils/geodetic";
import { mtm8ToGeodetic } from "../utils/measureUtils";

export interface POIData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  altitude: number;
  description?: string;
  layerName?: string;
  points?: Array<{ x: number; y: number; z: number }>;
  [key: string]: any;
}

interface POIOverlayProps {
  viewerRef: React.MutableRefObject<any>;
  pois: POIData[];
}

interface ScreenPOI {
  id: string;
  x: number;
  y: number;
  visible: boolean;
  distance: number;
}

function geodeticToWorld(
  lat: number,
  lon: number,
  alt: number,
  obsLat: number,
  obsLon: number,
  obsAlt: number
): THREE.Vector3 {
  const { east, north, up } = geodeticToENU(lat, lon, alt, obsLat, obsLon, obsAlt);
  const world = new THREE.Vector3(east, north, up);
  world.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
  return world;
}

function getWorldFromAPIProjectedPoint(
  poi: POIData,
  obsLat: number,
  obsLon: number,
  obsAlt: number
): THREE.Vector3 | null {
  const p = poi.points?.[0];
    if (
      !p ||
      !Number.isFinite(p.x) ||
      !Number.isFinite(p.y) ||
      !Number.isFinite(p.z)
    ) {
      return null;
    }

  const { latitude, longitude, altitude } = mtm8ToGeodetic(p.x, p.y, p.z);

  return geodeticToWorld(latitude, longitude, altitude, obsLat, obsLon, obsAlt);
}

function getWorldFromPOIGeodetic(
  poi: POIData,
  obsLat: number,
  obsLon: number,
  obsAlt: number
): THREE.Vector3 {
  return geodeticToWorld(
    poi.latitude,
    poi.longitude,
    poi.altitude,
    obsLat,
    obsLon,
    obsAlt
  );
}

function projectToScreen(
  worldPos: THREE.Vector3,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement
) {
  const vec = worldPos.clone().project(camera);
  const rect = canvas.getBoundingClientRect();

  const x = rect.left + (vec.x * 0.5 + 0.5) * rect.width;
  const y = rect.top + (-vec.y * 0.5 + 0.5) * rect.height;

  return {
    x,
    y,
    visible:
      vec.x >= -1 &&
      vec.x <= 1 &&
      vec.y >= -1 &&
      vec.y <= 1 &&
      vec.z >= -1 &&
      vec.z <= 1,
  };
}

const POIOverlay: React.FC<POIOverlayProps> = ({ viewerRef, pois }) => {
  const [screenPOIs, setScreenPOIs] = useState<ScreenPOI[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [obsReady, setObsReady] = useState(false);
  const rafRef = useRef<number>(0);
  const worldPosMap = useRef<Map<string, THREE.Vector3>>(new Map());
  

  useEffect(() => {
    if (obsReady) return;

    const check = setInterval(() => {
      if (viewerRef.current?.observerCoordinates) {
        setObsReady(true);
        clearInterval(check);
      }
    }, 200);

    return () => clearInterval(check);
  }, [viewerRef, obsReady]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer?.observerCoordinates || !obsReady) return;

    const observerCoordinates = viewer.observerCoordinates;
    if (!observerCoordinates || observerCoordinates.length < 3) return;

    const [obsLat, obsLon, obsAlt] = observerCoordinates as [number, number, number];
    console.log("[POI] observerCoordinates:", obsLat, obsLon, obsAlt);

    const map = new Map<string, THREE.Vector3>();

    pois.forEach((poi) => {
      const worldFromPoints = getWorldFromAPIProjectedPoint(poi, obsLat, obsLon, obsAlt);
      const worldFromLatLon = getWorldFromPOIGeodetic(poi, obsLat, obsLon, obsAlt);

      const world = worldFromPoints ?? worldFromLatLon;

      const convertedFromPoints = poi.points?.[0]
        ? mtm8ToGeodetic(poi.points[0].x, poi.points[0].y, poi.points[0].z)
        : null;

      console.log(`[POIOverlay] ${poi.name}`, {
        source: worldFromPoints ? "points->mtm8->wgs84->world" : "latlon->world",
        apiPoints: poi.points?.[0]
          ? {
              x: poi.points[0].x.toFixed(4),
              y: poi.points[0].y.toFixed(4),
              z: poi.points[0].z.toFixed(4),
            }
          : null,
          convertedFromPoints: convertedFromPoints
          ? {
              lat: convertedFromPoints.latitude.toFixed(8),
              lon: convertedFromPoints.longitude.toFixed(8),
              alt: convertedFromPoints.altitude.toFixed(4),
            }
          : null,
        geodetic: {
          lat: poi.latitude?.toFixed?.(8),
          lon: poi.longitude?.toFixed?.(8),
          alt: poi.altitude?.toFixed?.(4),
        },
        chosenWorld: {
          x: world.x.toFixed(4),
          y: world.y.toFixed(4),
          z: world.z.toFixed(4),
        },
        debugLatLonWorld: {
          x: worldFromLatLon.x.toFixed(4),
          y: worldFromLatLon.y.toFixed(4),
          z: worldFromLatLon.z.toFixed(4),
        },
        deltaMeters: worldFromPoints
          ? {
              dx: (worldFromPoints.x - worldFromLatLon.x).toFixed(4),
              dy: (worldFromPoints.y - worldFromLatLon.y).toFixed(4),
              dz: (worldFromPoints.z - worldFromLatLon.z).toFixed(4),
              dist: worldFromPoints.distanceTo(worldFromLatLon).toFixed(4),
            }
          : null,
      });

      map.set(poi.id, world);
    });

    worldPosMap.current = map;
  }, [pois, obsReady, viewerRef]);

  useEffect(() => {
    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera as THREE.Camera | undefined;
      const MAX_POI_DISTANCE_METERS = 30;

      if (canvas && camera && worldPosMap.current.size > 0) {
        const updated: ScreenPOI[] = [];

        worldPosMap.current.forEach((worldPos, id) => {
          const dist = (camera as any).position.distanceTo(worldPos);
          const proj = projectToScreen(worldPos, camera, canvas);

          updated.push({
            id,
            ...proj,
            distance: dist,
            visible: proj.visible && dist < MAX_POI_DISTANCE_METERS,
          });
        });

        setScreenPOIs(updated);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewerRef]);

  if (screenPOIs.length === 0) return null;

  return (
    <>
      {screenPOIs.map((sp) => {
        if (!sp.visible) return null;

        const poi = pois.find((p) => p.id === sp.id);
        if (!poi) return null;

        const isHovered = hoveredId === sp.id;

        const scale = Math.max(0.85, Math.min(1.2, 20 / Math.max(sp.distance, 1)));

        return (
          <div
            key={sp.id}
            className="fixed z-[999] select-none"
            style={{
              left: sp.x,
              top: sp.y,
              transform: "translate(-50%, -100%)",
              cursor: "pointer",
            }}
            onMouseEnter={() => setHoveredId(sp.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {isHovered && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginBottom: 8,
                    background: "rgba(10,18,32,0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 12,
                    padding: "10px 14px",
                    whiteSpace: "nowrap",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                    animation: "poi-fadein 0.15s ease",
                    pointerEvents: "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "white",
                        fontFamily: "Arial",
                      }}
                    >
                      {poi.name}
                    </div>
                  </div>

                  {poi.layerName && (
                    <div
                      style={{
                        fontSize: 10,
                        color: "white",
                        fontFamily: "Arial",
                        marginBottom: 4,
                      }}
                    >
                      {poi.layerName}
                    </div>
                  )}

                  {poi.points?.[0] ? (
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.5)",
                        fontFamily: "monospace",
                        lineHeight: 1.6,
                      }}
                    >
                      <div>X: {poi.points[0].x.toFixed(2)}</div>
                      <div>Y: {poi.points[0].y.toFixed(2)}</div>
                      <div>Z: {poi.points[0].z.toFixed(2)}</div>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.5)",
                        fontFamily: "monospace",
                        lineHeight: 1.6,
                      }}
                    >
                      <div>{poi.latitude.toFixed(6)}</div>
                      <div>{poi.longitude.toFixed(6)}</div>
                      <div>{poi.altitude.toFixed(2)}m</div>
                    </div>
                  )}
                </div>
              )}

              <svg
                width="24"
                height="32"
                viewBox="0 0 28 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  filter: isHovered
                    ? "drop-shadow(0 0 6px rgba(253,186,89,0.9))"
                    : "drop-shadow(0 2px 4px rgba(0,0,0,0.6))",
                  transform: `scale(${isHovered ? scale * 1.15 : scale})`,
                  transition: "transform 0.15s ease",
                }}
              >
                <defs>
                  <radialGradient id={"pinGrad-" + sp.id} cx="38%" cy="35%" r="60%">
                    <stop offset="0%" stopColor="#FFD580" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </radialGradient>
                </defs>
                <path
                  d="M14 2C8.477 2 4 6.477 4 12c0 7.732 10 24 10 24s10-16.268 10-24c0-5.523-4.477-10-10-10z"
                  fill={"url(#pinGrad-" + sp.id + ")"}
                  stroke="rgba(255,255,255,0.6)"
                  strokeWidth="1.5"
                />
                <circle cx="14" cy="12" r="4" fill="white" fillOpacity="0.9" />
              </svg>

              {isHovered && (
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    left: "50%",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "2px solid rgba(253,186,89,0.75)",
                    animation: "poi-pulse 1.4s ease-out infinite",
                    pointerEvents: "none",
                  }}
                />
              )}
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes poi-pulse {
          0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; }
          100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
        }
        @keyframes poi-fadein {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </>
  );
};

export default POIOverlay;